import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      console.log('Stock In API: Token obtained successfully');
      return idToken;
    }
    
    console.warn('Stock In API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Stock In API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Stock In API: Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const responseText = await response.text();
      let errorData = {};
      try {
        errorData = responseText ? JSON.parse(responseText) : {};
      } catch {
        errorData = { message: responseText || 'Unknown error' };
      }
      
      console.error(
        `Stock In API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : errorData.error || errorData.message || 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;

      // Log API error to logging service
      loggingService.logError(
        'api_error_stock_in',
        `Stock In API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[stockInApi] Failed to log error:', logError);
      });

      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      // Log authentication error to logging service
      loggingService.logError(
        'api_error_stock_in',
        `Stock In API authentication error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'AuthenticationError',
        }
      ).catch(logError => {
        console.warn('[stockInApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    console.error(`Stock In API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;

    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_stock_in',
      `Stock In API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[stockInApi] Failed to log error:', logError);
    });

    throw error;
  }
};

/**
 * API utility functions for Stock In Management.
 */
export const stockInApi = {
  /**
   * Creates a new stock-in entry.
   * POST /stock/in or /stock-in/create (adjust endpoint based on actual API)
   * @param {Object} stockInData - Stock-in data
   * @param {string} stockInData.date - Date of stock entry (ISO format or YYYY-MM-DD)
   * @param {string} stockInData.siteId - Site ID where stock is being added
   * @param {Array} stockInData.items - Array of stock items
   * @param {string} items[].stockName - Name of the stock item
   * @param {number} items[].quantityOrWeight - Quantity/weight of the item
   * @param {number} items[].pricePerUnit - Price per unit/kg
   * @param {number} items[].totalPrice - Total price (calculated)
   * @param {string} items[].vendorName - Vendor name
   * @param {string} items[].expDate - Expiration date (ISO format or YYYY-MM-DD)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createStockIn: async (stockInData) => {
    try {
      // Prepare request body - backend typically expects snake_case
      const requestBody = {
        date: stockInData.date,
        site_id: stockInData.siteId,
        items: stockInData.items.map(item => ({
          stock_name: item.stockName,
          quantity_or_weight: parseFloat(item.quantityOrWeight) || 0,
          price_per_unit: parseFloat(item.pricePerUnit) || 0,
          total_price: parseFloat(item.totalPrice) || 0,
          vendor_name: item.vendorName,
          exp_date: item.expDate,
        })),
      };

      // Try common endpoint patterns
      const endpoints = [
        '/stock/in',
        '/stock-in/create',
        '/stock/in/create',
        '/inventory/stock-in',
      ];

      let lastError = null;
      for (const endpoint of endpoints) {
        try {
          console.log(`Stock In API: Trying endpoint: ${endpoint}`);
          const response = await makeApiRequest(endpoint, 'POST', requestBody);
          const responseData = await response.json();

          console.log('Stock In API: Create response:', responseData);

          return {
            success: true,
            data: responseData,
          };
        } catch (error) {
          // If it's a 404, try next endpoint
          if (error.status === 404) {
            lastError = error;
            continue;
          }
          // For other errors, throw immediately
          throw error;
        }
      }

      // If all endpoints failed with 404, throw the last error
      if (lastError) {
        throw lastError;
      }
    } catch (error) {
      console.error('Stock In API: Error in createStockIn:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create stock-in entry',
      };
    }
  },

  /**
   * Updates an existing stock-in entry.
   * PUT /stock/in/{id} or /stock-in/update/{id}
   * @param {string} stockInId - The ID of the stock-in entry to update
   * @param {Object} stockInData - Updated stock-in data (same structure as createStockIn)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  updateStockIn: async (stockInId, stockInData) => {
    try {
      const requestBody = {
        date: stockInData.date,
        site_id: stockInData.siteId,
        items: stockInData.items.map(item => ({
          stock_name: item.stockName,
          quantity_or_weight: parseFloat(item.quantityOrWeight) || 0,
          price_per_unit: parseFloat(item.pricePerUnit) || 0,
          total_price: parseFloat(item.totalPrice) || 0,
          vendor_name: item.vendorName,
          exp_date: item.expDate,
          ...(item.id ? { id: item.id } : {}), // Include item ID if updating existing items
        })),
      };

      const endpoints = [
        `/stock/in/${stockInId}`,
        `/stock-in/update/${stockInId}`,
        `/stock/in/update/${stockInId}`,
        `/inventory/stock-in/${stockInId}`,
      ];

      let lastError = null;
      for (const endpoint of endpoints) {
        try {
          console.log(`Stock In API: Trying update endpoint: ${endpoint}`);
          const response = await makeApiRequest(endpoint, 'PUT', requestBody);
          const responseData = await response.json();

          console.log('Stock In API: Update response:', responseData);

          return {
            success: true,
            data: responseData,
          };
        } catch (error) {
          if (error.status === 404) {
            lastError = error;
            continue;
          }
          throw error;
        }
      }

      if (lastError) {
        throw lastError;
      }
    } catch (error) {
      console.error('Stock In API: Error in updateStockIn:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to update stock-in entry',
      };
    }
  },

  /**
   * Fetches stock-in transactions filtered by site and month.
   * GET /stock/stock-in
   * @param {Object} params - Query parameters
   * @param {string} params.site_id - Site identifier (required)
   * @param {string} params.month - Month in format YYYY-MM (required, e.g., "2025-11")
   * @param {number} [params.limit] - Pagination limit (default: 100, max: 500)
   * @param {string} [params.last_key] - Pagination token from previous response
   * @param {boolean} [params.include_voided] - Include voided entries in response (default: false)
   * @returns {Promise<Object>} Object containing success status, stock_in_records, and summary
   */
  getStockInView: async ({ site_id, month, limit, last_key, include_voided } = {}) => {
    try {
      if (!site_id || !month) {
        return {
          success: false,
          error: 'Validation error',
          message: 'site_id and month are required',
          stock_in_records: [],
          summary: null,
        };
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('site_id', site_id);
      queryParams.append('month', month);
      
      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (last_key !== undefined && last_key !== null) {
        queryParams.append('last_key', last_key);
      }
      if (include_voided !== undefined && include_voided !== null) {
        queryParams.append('include_voided', include_voided.toString());
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/stock-in?${queryString}`;

      console.log(`Stock In API: Fetching stock-in view: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Stock In API: Stock-in view response:', responseData);

      // Handle response format
      const stock_in_records = responseData.stock_in_records || 
                              responseData.records || 
                              responseData.data || 
                              (Array.isArray(responseData) ? responseData : []);

      // Normalize exp_date and vendor_id in records
      const normalizedRecords = stock_in_records.map(record => ({
        ...record,
        exp_date: record.exp_date || record.expiry_date,
        // Keep expiry_date for backward compatibility
        expiry_date: record.exp_date || record.expiry_date,
        // vendor_name is enriched from vendor_id if missing
        vendor_name: record.vendor_name || (record.vendor_id ? undefined : record.vendor_name),
      }));

      return {
        success: true,
        data: responseData,
        stock_in_records: normalizedRecords,
        summary: responseData.summary || null,
        last_key: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Stock In API: Error in getStockInView:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          stock_in_records: [],
          summary: null,
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          stock_in_records: [],
          summary: null,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch stock-in entries',
        stock_in_records: [],
        summary: null,
      };
    }
  },

  /**
   * Updates an existing stock-in ledger entry by ledger_id.
   * PUT /stock/stock-in/{id}
   * @param {string} ledgerId - The ledger_id of the stock-in entry to update
   * @param {Object} stockInData - Stock-in transaction data
   * @param {number} stockInData.quantity - Quantity to update (required, > 0)
   * @param {number} [stockInData.unit_cost] - Unit cost for ledger (default: 0)
   * @param {string} [stockInData.vendor_id] - Vendor ID (preferred over vendor_name if available)
   * @param {string} [stockInData.vendor_name] - Vendor name (used if vendor_id not provided)
   * @param {string} [stockInData.expiry_date] - Expiry date in ISO format (e.g., "2026-01-31")
   * @param {string} [stockInData.reference_id] - PO number, invoice, or other reference
   * @param {string} [stockInData.notes] - Additional notes
   * @returns {Promise<Object>} Object containing success status and updated entry data
   */
  updateStockInEntry: async (ledgerId, stockInData) => {
    try {
      if (!ledgerId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Ledger ID is required',
        };
      }

      const requestBody = {
        quantity: parseFloat(stockInData.quantity) || 0,
        unit_cost: parseFloat(stockInData.unit_cost) || 0,
        ...(stockInData.vendor_id ? { vendor_id: stockInData.vendor_id } : {}),
        ...(stockInData.vendor_name ? { vendor_name: stockInData.vendor_name } : {}),
        ...(stockInData.expiry_date ? { expiry_date: stockInData.expiry_date } : {}),
        ...(stockInData.reference_id ? { reference_id: stockInData.reference_id } : {}),
        ...(stockInData.notes ? { notes: stockInData.notes } : {}),
      };

      const endpoint = `/stock/stock-in/${ledgerId}`;
      
      console.log(`Stock In API: Updating stock-in entry ${ledgerId}`);
      console.log(`Stock In API: Request body:`, requestBody);
      const response = await makeApiRequest(endpoint, 'PUT', requestBody);
      const responseData = await response.json();

      console.log('Stock In API: Update stock-in entry response:', responseData);

      return {
        success: true,
        data: responseData,
        entry: responseData.entry,
        message: responseData.message || 'Stock-in entry updated',
      };
    } catch (error) {
      console.error('Stock In API: Error in updateStockInEntry:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to update stock-in entry',
      };
    }
  },

  /**
   * Fetches stock-in entries with optional filtering (legacy method, kept for backward compatibility).
   * GET /stock/in or /stock-in/list
   * @param {Object} params - Optional parameters for filtering
   * @param {string} [params.siteId] - Filter by site ID
   * @param {string} [params.date] - Filter by specific date (YYYY-MM-DD)
   * @param {string} [params.startDate] - Filter entries after this date
   * @param {string} [params.endDate] - Filter entries before this date
   * @param {string} [params.month] - Filter by month (YYYY-MM)
   * @param {number} [params.limit] - Maximum number of entries to return
   * @returns {Promise<Object>} Object containing success status and data
   * @deprecated Use getStockInView instead for the new API
   */
  getStockInEntries: async ({
    siteId,
    date,
    startDate,
    endDate,
    month,
    limit,
  } = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (siteId !== undefined && siteId !== null) {
        queryParams.append('site_id', siteId);
      }
      if (date !== undefined && date !== null) {
        queryParams.append('date', date);
      }
      if (startDate !== undefined && startDate !== null) {
        queryParams.append('start_date', startDate);
      }
      if (endDate !== undefined && endDate !== null) {
        queryParams.append('end_date', endDate);
      }
      if (month !== undefined && month !== null) {
        queryParams.append('month', month);
      }
      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }

      const queryString = queryParams.toString();
      const endpoints = [
        `/stock/in${queryString ? `?${queryString}` : ''}`,
        `/stock-in/list${queryString ? `?${queryString}` : ''}`,
        `/stock/in/list${queryString ? `?${queryString}` : ''}`,
        `/inventory/stock-in${queryString ? `?${queryString}` : ''}`,
      ];

      let lastError = null;
      for (const endpoint of endpoints) {
        try {
          console.log(`Stock In API: Trying get endpoint: ${endpoint}`);
          const response = await makeApiRequest(endpoint, 'GET');
          const responseData = await response.json();

          console.log('Stock In API: Get entries response:', responseData);

          // Handle different response formats
          const entries = responseData.entries || 
                         responseData.data || 
                         responseData.items || 
                         (Array.isArray(responseData) ? responseData : []);

          return {
            success: true,
            data: responseData,
            entries: entries,
          };
        } catch (error) {
          if (error.status === 404) {
            lastError = error;
            continue;
          }
          throw error;
        }
      }

      if (lastError) {
        throw lastError;
      }
    } catch (error) {
      console.error('Stock In API: Error in getStockInEntries:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          entries: [],
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          entries: [],
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch stock-in entries',
        entries: [],
      };
    }
  },

  /**
   * Creates a stock-in transaction for a specific stock item.
   * POST /stock/items/{id}/stock-in
   * @param {string} stockItemId - The stock_item_id of the item to update
   * @param {Object} stockInData - Stock-in transaction data
   * @param {number} stockInData.quantity - Quantity to add (required, > 0)
   * @param {number} [stockInData.unit_cost] - Unit cost for ledger (default: 0)
   * @param {string} [stockInData.site_id] - Site ID (falls back to existing item's site_id if not provided)
   * @param {string} [stockInData.site_name] - Site name
   * @param {string} [stockInData.vendor_id] - Vendor ID (optional, preferred over vendor_name if available)
   * @param {string} [stockInData.vendor_name] - Vendor name (used if vendor_id not provided)
   * @param {string} [stockInData.expiry_date] - Expiry date in ISO format (e.g., "2026-01-31")
   * @param {string} [stockInData.reference_id] - PO number, invoice, or other reference
   * @param {string} [stockInData.notes] - Additional notes
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createStockInTransaction: async (stockItemId, stockInData) => {
    try {
      const requestBody = {
        quantity: parseFloat(stockInData.quantity) || 0,
        unit_cost: parseFloat(stockInData.unit_cost) || 0,
        ...(stockInData.site_id ? { site_id: stockInData.site_id } : {}),
        ...(stockInData.site_name ? { site_name: stockInData.site_name } : {}),
        ...(stockInData.vendor_id ? { vendor_id: stockInData.vendor_id } : {}),
        ...(stockInData.vendor_name ? { vendor_name: stockInData.vendor_name } : {}),
        ...(stockInData.expiry_date ? { expiry_date: stockInData.expiry_date } : {}),
        ...(stockInData.reference_id ? { reference_id: stockInData.reference_id } : {}),
        ...(stockInData.notes ? { notes: stockInData.notes } : {}),
      };

      const endpoint = `/stock/items/${stockItemId}/stock-in`;
      
      console.log(`Stock In API: Creating stock-in transaction for item ${stockItemId}`);
      const response = await makeApiRequest(endpoint, 'POST', requestBody);
      const responseData = await response.json();

      console.log('Stock In API: Stock-in transaction response:', responseData);

      return {
        success: true,
        data: responseData,
        item: responseData.item,
        ledger_entry: responseData.ledger_entry,
      };
    } catch (error) {
      console.error('Stock In API: Error in createStockInTransaction:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create stock-in transaction',
      };
    }
  },

  /**
   * Gets or searches for stock items.
   * This is a helper to find stock_item_id by name or other criteria.
   * GET /stock/items or /stock/items/search
   * @param {Object} params - Search parameters
   * @param {string} [params.name] - Search by item name
   * @param {string} [params.site_id] - Filter by site ID
   * @returns {Promise<Object>} Object containing success status and items array
   */
  getStockItems: async ({ name, site_id } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (name !== undefined && name !== null) {
        queryParams.append('name', name);
      }
      if (site_id !== undefined && site_id !== null) {
        queryParams.append('site_id', site_id);
      }

      const queryString = queryParams.toString();
      const endpoints = [
        `/stock/items${queryString ? `?${queryString}` : ''}`,
      ];

      let lastError = null;
      for (const endpoint of endpoints) {
        try {
          console.log(`Stock In API: Trying get stock items endpoint: ${endpoint}`);
          const response = await makeApiRequest(endpoint, 'GET');
          const responseData = await response.json();

          console.log('Stock In API: Get stock items response:', responseData);

          // Handle different response formats
          const items = responseData.items || 
                       responseData.data || 
                       (Array.isArray(responseData) ? responseData : []);

          // Prefer exp_date over expiry_date for each item (exp_date is now an alias)
          const normalizedItems = items.map(item => ({
            ...item,
            exp_date: item.exp_date || item.expiry_date,
            // Keep expiry_date for backward compatibility
            expiry_date: item.exp_date || item.expiry_date,
          }));

          return {
            success: true,
            data: responseData,
            items: normalizedItems,
          };
        } catch (error) {
          if (error.status === 404) {
            lastError = error;
            continue;
          }
          throw error;
        }
      }

      if (lastError) {
        throw lastError;
      }
    } catch (error) {
      console.error('Stock In API: Error in getStockItems:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          items: [],
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          items: [],
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch stock items',
        items: [],
      };
    }
  },

  /**
   * Fetches available sites.
   * Uses the same endpoint as inventory onboarding.
   * GET /site/getall
   * @returns {Promise<Array>} Array of site objects
   */
  getSites: async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error(
          'Authentication token is missing. Please ensure a user is logged in.'
        );
      }

      console.log(`Stock In API: Calling endpoint: ${API_BASE_URL}/site/getall`);

      const response = await fetch(`${API_BASE_URL}/site/getall`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.log(`Stock In API: HTTP Error: ${response.status} ${response.statusText}`);
        console.log(`Stock In API: Response Body:`, responseText);

        let errorMessage;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage =
            errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`;
        } catch {
          errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Stock In API: Sites fetched successfully:', result);
      
      // Handle different response formats
      if (Array.isArray(result)) {
        return result;
      } else if (result.sites && Array.isArray(result.sites)) {
        return result.sites;
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      }
      
      return result;
    } catch (error) {
      console.error('Stock In API: Error fetching sites:', error);
      // Return empty array on error to prevent form from breaking
      return [];
    }
  },

  /**
   * Creates a new stock item.
   * POST /stock/items
   * @param {Object} stockItemData - Stock item data according to API spec
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createStockItem: async (stockItemData) => {
    try {
      const endpoint = '/stock/items';
      
      console.log('Stock API: Creating stock item:', stockItemData);
      const response = await makeApiRequest(endpoint, 'POST', stockItemData);
      const responseData = await response.json();

      console.log('Stock API: Create stock item response:', responseData);

      return {
        success: true,
        data: responseData,
        item: responseData.item,
      };
    } catch (error) {
      console.error('Stock API: Error in createStockItem:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create stock item',
      };
    }
  },

  /**
   * Lists stock items with optional filters.
   * GET /stock/items
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Page size (default: 50, max: 100)
   * @param {string} [params.last_key] - Pagination token
   * @param {string} [params.name_contains] - Case-insensitive substring filter on item_name
   * @param {string} [params.vendor_name] - Case-insensitive substring filter on vendor_name
   * @param {string} [params.low_stock] - When "true", show only items where current_quantity <= reorder_threshold
   * @param {string} [params.site_id] - Filter by site identifier
   * @param {string} [params.is_active] - Filter by active status
   * @param {string} [params.current_quantity_gt_zero] - When "true", show only items with quantity > 0
   * @returns {Promise<Object>} Object containing success status, items array, and pagination info
   */
  listStockItems: async ({ limit, last_key, name_contains, vendor_name, low_stock, site_id, is_active, current_quantity_gt_zero } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (last_key !== undefined && last_key !== null) {
        queryParams.append('last_key', last_key);
      }
      if (name_contains !== undefined && name_contains !== null) {
        queryParams.append('name_contains', name_contains);
      }
      if (vendor_name !== undefined && vendor_name !== null) {
        queryParams.append('vendor_name', vendor_name);
      }
      if (low_stock !== undefined && low_stock !== null) {
        queryParams.append('low_stock', low_stock.toString());
      }
      if (site_id !== undefined && site_id !== null) {
        queryParams.append('site_id', site_id);
      }
      if (is_active !== undefined && is_active !== null) {
        queryParams.append('is_active', is_active.toString());
      }
      if (current_quantity_gt_zero !== undefined && current_quantity_gt_zero !== null) {
        queryParams.append('current_quantity_gt_zero', current_quantity_gt_zero.toString());
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/items${queryString ? `?${queryString}` : ''}`;

      console.log(`Stock API: Listing stock items: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Stock API: List stock items response:', responseData);

      return {
        success: true,
        data: responseData,
        items: responseData.items || [],
        count: responseData.count || 0,
        last_key: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Stock API: Error in listStockItems:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          items: [],
          count: 0,
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          items: [],
          count: 0,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to list stock items',
        items: [],
        count: 0,
      };
    }
  },

  /**
   * Gets a single stock item by ID.
   * GET /stock/items/{id}
   * @param {string} stockItemId - Stock item identifier
   * @returns {Promise<Object>} Object containing success status and item data
   */
  getStockItem: async (stockItemId) => {
    try {
      if (!stockItemId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Stock item ID is required',
        };
      }

      const endpoint = `/stock/items/${stockItemId}`;
      
      console.log(`Stock API: Getting stock item: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Stock API: Get stock item response:', responseData);

      return {
        success: true,
        data: responseData,
        item: responseData.item,
      };
    } catch (error) {
      console.error('Stock API: Error in getStockItem:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to get stock item',
      };
    }
  },

  /**
   * Updates a stock item (partial updates supported).
   * PUT /stock/items/{id}
   * @param {string} stockItemId - Stock item identifier
   * @param {Object} stockItemData - Partial stock item data to update
   * @returns {Promise<Object>} Object containing success status and updated item data
   */
  updateStockItem: async (stockItemId, stockItemData) => {
    try {
      if (!stockItemId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Stock item ID is required',
        };
      }

      const endpoint = `/stock/items/${stockItemId}`;
      
      console.log(`Stock API: Updating stock item: ${endpoint}`, stockItemData);
      const response = await makeApiRequest(endpoint, 'PUT', stockItemData);
      const responseData = await response.json();

      console.log('Stock API: Update stock item response:', responseData);

      return {
        success: true,
        data: responseData,
        item: responseData.item,
      };
    } catch (error) {
      console.error('Stock API: Error in updateStockItem:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to update stock item',
      };
    }
  },

  /**
   * Deactivates a stock item (soft delete).
   * DELETE /stock/items/{id}
   * @param {string} stockItemId - Stock item identifier
   * @returns {Promise<Object>} Object containing success status and deactivated item data
   */
  deactivateStockItem: async (stockItemId) => {
    try {
      if (!stockItemId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Stock item ID is required',
        };
      }

      const endpoint = `/stock/items/${stockItemId}`;
      
      console.log(`Stock API: Deactivating stock item: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'DELETE');
      const responseData = await response.json();

      console.log('Stock API: Deactivate stock item response:', responseData);

      return {
        success: true,
        data: responseData,
        item: responseData.item,
      };
    } catch (error) {
      console.error('Stock API: Error in deactivateStockItem:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to deactivate stock item',
      };
    }
  },

  /**
   * Voids a stock-in entry (recommended "remove a row" behavior).
   * POST /stock/stock-in/{id}/void
   * Reverses inventory impact by deducting the stock quantity that was added by that stock-in.
   * Marks ledger entry as voided (is_voided=true, voided_at, voided_by, optional void_reason).
   * @param {string} ledgerId - Ledger entry ID (ledger_id)
   * @param {Object} [options] - Optional void parameters
   * @param {string} [options.reason] - Optional reason for voiding
   * @returns {Promise<Object>} Object containing success status and response data
   */
  voidStockInEntry: async (ledgerId, options = {}) => {
    try {
      if (!ledgerId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Ledger entry ID is required',
        };
      }

      const requestBody = {};
      if (options.reason) {
        requestBody.reason = options.reason;
      }

      const endpoint = `/stock/stock-in/${ledgerId}/void`;
      
      console.log(`Stock In API: Voiding stock-in entry ${ledgerId}`);
      console.log(`Stock In API: Request body:`, requestBody);
      const response = await makeApiRequest(endpoint, 'POST', Object.keys(requestBody).length > 0 ? requestBody : null);
      const responseData = await response.json();

      console.log('Stock In API: Void stock-in entry response:', responseData);

      return {
        success: true,
        data: responseData,
        entry: responseData.entry,
        message: responseData.message || 'Stock-in entry voided successfully',
      };
    } catch (error) {
      console.error('Stock In API: Error in voidStockInEntry:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to void stock-in entry',
      };
    }
  },

  /**
   * Deletes a stock-in entry.
   * DELETE /stock/stock-in/{ledger_id}
   * @param {string} ledgerId - Ledger entry ID (ledger_id)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  deleteStockInEntry: async (ledgerId) => {
    try {
      if (!ledgerId) {
        throw new Error('Ledger entry ID is required for deletion');
      }

      console.log('🗑️ Stock In API: Deleting stock-in entry');
      console.log('🗑️ Stock In API: Ledger ID:', ledgerId);
      
      const endpoint = `/stock/stock-in/${ledgerId}`;
      console.log('🗑️ Stock In API: Delete endpoint:', endpoint);
      console.log('🗑️ Stock In API: Full URL:', `${API_BASE_URL}${endpoint}`);
      
      const response = await makeApiRequest(endpoint, 'DELETE', null);
      const responseData = await response.json();

      console.log('🗑️ Stock In API: Delete response status:', response.status);
      console.log('🗑️ Stock In API: Delete response data:', JSON.stringify(responseData, null, 2));

      // Check if the response indicates success
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          data: responseData,
          message: responseData.message || 'Stock-in entry deleted successfully',
          ledger_id: responseData.ledger_id || ledgerId,
        };
      } else {
        throw new Error(`Delete failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Stock In API: Error in deleteStockInEntry:', error);
      console.error('Stock In API: Error details:', {
        message: error.message,
        status: error.status,
        endpoint: error.endpoint,
        responseData: error.responseData,
      });
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.responseData?.message || error.message || 'Failed to delete stock-in entry',
        status: error.status,
      };
    }
  },

  /**
   * Gets a presigned URL for uploading stock item image.
   * POST /stock/items/{stock_item_id}/image/presign
   * @param {string} stockItemId - Stock item identifier (must exist)
   * @param {string} [contentType] - MIME type (default: "image/jpeg")
   * @returns {Promise<Object>} Object containing success status and presigned URL data
   */
  presignStockItemImageUpload: async (stockItemId, contentType = 'image/jpeg') => {
    try {
      if (!stockItemId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Stock item ID is required',
        };
      }

      const queryParams = new URLSearchParams();
      if (contentType) {
        queryParams.append('content_type', contentType);
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/items/${stockItemId}/image/presign${queryString ? `?${queryString}` : ''}`;
      
      console.log(`Stock API: Getting presigned URL for image: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'POST');
      const responseData = await response.json();

      console.log('Stock API: Presign image response:', responseData);

      return {
        success: true,
        data: responseData,
        presigned_post: responseData.presigned_post,
        object_key: responseData.object_key,
        content_type: responseData.content_type,
      };
    } catch (error) {
      console.error('Stock API: Error in presignStockItemImageUpload:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to get presigned URL for image',
      };
    }
  },
};

